import React, { createContext, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../api';
import {
  getDefaultStayDates,
  getNightsBetween,
  getTodayString,
  isValidStayRange,
  normalizeStayPayload,
  toDateInputValue,
  addDays,
} from '../utils/dateUtils';
import { getFallbackRoomImage, getRoomImageUrl } from '../utils/roomImageUtils';

export const RoomContext = createContext();

const preloadRoomImages = (rooms) => {
  rooms.forEach((room) => {
    const img = new Image();
    img.onerror = () => {
      img.src = getFallbackRoomImage(room.id);
    };
    img.src = getRoomImageUrl(room);
  });
};

export const RoomProvider = ({ children }) => {
  const defaultDates = getDefaultStayDates();

  const [rooms, setRooms] = useState([]);
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(0);
  const [totalGuests, setTotalGuests] = useState(2);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);

  const [selectedRoomDetails, setSelectedRoomDetails] = useState({
    checkInDate: defaultDates.checkInStr,
    checkOutDate: defaultDates.checkOutStr,
    roomType: '',
    adults: 2,
    kids: 0,
    price: 0,
    mealPlan: null,
    mealCost: 0,
    taxes: 0,
    totalAmount: 0,
    numberOfDays: defaultDates.numberOfDays,
  });

  const fetchAvailableRooms = useCallback(async (checkIn, checkOut, guestCount) => {
    const response = await axios.get(`${API_BASE_URL}/inventory/available`, {
      params: {
        checkIn,
        checkOut,
        maxPerson: guestCount,
      },
    });
    return response.data || [];
  }, []);

  const loadAvailableRooms = useCallback(
    async ({ checkIn, checkOut, guestCount, markSearched = true }) => {
      const { checkInDate, checkOutDate, numberOfDays } = normalizeStayPayload(checkIn, checkOut);

      if (!isValidStayRange(checkInDate, checkOutDate)) {
        alert('Check-out date must be later than check-in date.');
        return [];
      }

      setSearchLoading(true);
      try {
        const availableRooms = await fetchAvailableRooms(checkInDate, checkOutDate, guestCount);
        setRooms(availableRooms);
        preloadRoomImages(availableRooms);
        setSelectedRoomDetails((prev) => ({
          ...prev,
          checkInDate,
          checkOutDate,
          adults,
          kids,
          numberOfDays,
        }));
        if (markSearched) {
          setIsSearchTriggered(true);
        }
        return availableRooms;
      } catch (error) {
        console.error('Error fetching available rooms:', error);
        setRooms([]);
        return [];
      } finally {
        setSearchLoading(false);
        setRoomsLoading(false);
      }
    },
    [adults, kids, fetchAvailableRooms]
  );

  useEffect(() => {
    setTotalGuests(Number(adults) + Number(kids));
  }, [adults, kids]);

  const handleClick = (e, checkInDate, checkOutDate) => {
    e.preventDefault();

    const resolvedCheckIn = toDateInputValue(
      checkInDate || getTodayString()
    );
    const resolvedCheckOut = toDateInputValue(
      checkOutDate || addDays(resolvedCheckIn, 1)
    );

    if (!isValidStayRange(resolvedCheckIn, resolvedCheckOut)) {
      alert('Check-out date must be later than check-in date.');
      return false;
    }

    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      checkInDate: resolvedCheckIn,
      checkOutDate: resolvedCheckOut,
      adults,
      kids,
      numberOfDays: getNightsBetween(resolvedCheckIn, resolvedCheckOut),
    }));

    return true;
  };

  const updateRoomDetails = (details) => {
    setSelectedRoomDetails((prevDetails) => ({
      ...prevDetails,
      ...details,
    }));
  };

  return (
    <RoomContext.Provider
      value={{
        rooms,
        setRooms,
        adults,
        setAdults,
        kids,
        setKids,
        totalGuests,
        handleClick,
        roomsLoading,
        searchLoading,
        setSearchLoading,
        isSearchTriggered,
        setIsSearchTriggered,
        fetchAvailableRooms,
        loadAvailableRooms,
        selectedRoomDetails,
        setSelectedRoomDetails,
        updateRoomDetails,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};
